import React, { useState } from 'react';
import {
  Stack,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { IconLibrary } from '@product-framework/index';
import formatTimeZoneDateTime from '@product-framework/utils/formatTimeZoneDateTime';
import { useLocalization, SnackbarApiErrorMessage } from '@ui-framework/index';

function fileName(original) {
  return original?.fileName || original?.id || '';
}

function createdDateTime(original) {
  const { formatMessage } = useLocalization();

  const { localDatetime, systemDatetime } = formatTimeZoneDateTime(
    original?.createdDateTime,
  );

  return (
    <Stack component="span" spacing={0.25} sx={{ py: 0.25 }}>
      <Typography component="span" variant="body2" sx={{ display: 'block' }}>
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ mr: 0.75 }}>
          {formatMessage({
            id: 'CLAIM_PAYMENT_EXPORT_LIST_LOCAL_DATETIME',
            defaultMessage: 'Your Time',
          })}
          :
        </Typography>
        {localDatetime}
      </Typography>
      <Typography component="span" variant="body2" sx={{ display: 'block' }}>
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ mr: 0.75 }}>
          {formatMessage({
            id: 'CLAIM_PAYMENT_EXPORT_LIST_SYSTEM_DATETIME',
            defaultMessage: 'System Time',
          })}
          :
        </Typography>
        {systemDatetime}
      </Typography>
    </Stack>
  );
}

function ClaimPaymentExportDownloadButton({
  row,
  getApiUrl,
  get,
  formatMessage,
  snackbar,
}) {
  const [loading, setLoading] = useState(false);

  const getPresignedUrl = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/claim-payment-exports/${row?.id}/download`,
    );
    if (response?.ok) {
      return response.data.presignedUrl;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const handleDownload = async () => {
    setLoading(true);
    const presignedUrl = await getPresignedUrl();
    if (presignedUrl) {
      window.open(presignedUrl, '_blank', 'noopener,noreferrer');
    } else {
      snackbar.showMessage({
        type: 'error',
        data: (
          <SnackbarApiErrorMessage
            errors={[
              {
                message: formatMessage({
                  id: 'CLAIM_PAYMENT_EXPORT_DOWNLOAD_FAILED',
                  defaultMessage:
                    'Failed to download claim payment export file',
                }),
              },
            ]}
          />
        ),
      });
    }
    setLoading(false);
  };

  return (
    <Tooltip
      title={formatMessage({
        id: 'CLAIM_PAYMENT_EXPORT_DOWNLOAD_TOOLTIP',
        defaultMessage: 'Download export file',
      })}>
      <IconButton size="small" onClick={handleDownload} disabled={loading}>
        {loading ? (
          <CircularProgress color="inherit" size={18} />
        ) : (
          <IconLibrary.ClaimPaymentExportDownload />
        )}
      </IconButton>
    </Tooltip>
  );
}

function download(original, _props, accessorFunctionsAdditional) {
  const { getApiUrl, get, formatMessage, snackbar } =
    accessorFunctionsAdditional;

  return (
    <ClaimPaymentExportDownloadButton
      row={original}
      getApiUrl={getApiUrl}
      get={get}
      formatMessage={formatMessage}
      snackbar={snackbar}
    />
  );
}

function ClaimPaymentExportPreviewButton({
  row,
  getApiUrl,
  get,
  formatMessage,
  snackbar,
}) {
  const [loading, setLoading] = useState(false);

  const getPresignedUrl = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/claim-payment-exports/${row?.id}/preview`,
    );
    if (response?.ok) {
      return response.data.presignedUrl;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const handlePreview = async () => {
    setLoading(true);
    const presignedUrl = await getPresignedUrl();
    if (presignedUrl) {
      window.open(presignedUrl, '_blank');
    } else {
      snackbar.showMessage({
        type: 'error',
        data: (
          <SnackbarApiErrorMessage
            errors={[
              {
                message: formatMessage({
                  id: 'CLAIM_PAYMENT_EXPORT_PREVIEW_FAILED',
                  defaultMessage: 'Failed to generate preview of export file',
                }),
              },
            ]}
          />
        ),
      });
    }
    setLoading(false);
  };

  return (
    <Tooltip
      title={formatMessage({
        id: 'CLAIM_PAYMENT_EXPORT_PREVIEW_TOOLTIP',
        defaultMessage: 'Open preview in a new tab',
      })}>
      <IconButton size="small" onClick={handlePreview} disabled={loading}>
        {loading ? (
          <CircularProgress color="inherit" size={18} />
        ) : (
          <IconLibrary.ClaimPaymentExportPreview />
        )}
      </IconButton>
    </Tooltip>
  );
}

function preview(original, _props, accessorFunctionsAdditional) {
  const { getApiUrl, get, formatMessage, snackbar } =
    accessorFunctionsAdditional;

  return (
    <ClaimPaymentExportPreviewButton
      row={original}
      getApiUrl={getApiUrl}
      get={get}
      formatMessage={formatMessage}
      snackbar={snackbar}
    />
  );
}

export { fileName, createdDateTime, download, preview };
